import { DataCenterFinancialSnapshot } from '../../financial/DataCenterFinancialSnapshot';
/**
 * CapexOpexBreakdownSection (FIN-2) — CAPEX / OPEX cost framing + per-pathway
 * LCOE (editorial).
 *
 * READ-ONLY over the persisted deterministic stamps:
 *   - `report.financialAnalysis.headline` (CAPEX/kW, annual generation, LCOE)
 *   - `report.pathwayComparisonAtGen.pathways` (per-pathway LCOE rows — the
 *     report's "per-phase / per-pathway LCOE" surface, since the project-finance
 *     stamp does not persist a per-phase decomposition).
 *
 * W2a port: cost-intensity strip → EditorialKpiRail; the ad-hoc per-pathway
 * table → EditorialDataTable (mono small-caps header, hairline rows, numeric
 * LCOE column). Identical data + formatting via financialFormat.
 *
 * GAP: a CAPEX/OPEX cost COMPOSITION (per-pathway or per-phase capex/opex
 * split) is NOT generated — the stamp carries intensity figures only, and
 * per-pathway LCOEs are competing ALTERNATIVES, not same-unit components of
 * one whole. No EditorialStackBar here (W1d unit-guard discipline): never
 * stack alternatives or mixed bases. NEVER re-derives LCOE/CAPEX math.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { lcoeBasisLabel } from '../../../../lib/lcoeBasis';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialDataTable, EditorialKpiRail } from '../../report/editorial';
import { FIN_MONO, fmtMwh, fmtUsdPerKw, fmtUsdPerMwh } from '../../../../lib/reportRendering/financialFormat';

/** Reader-facing classification label for the per-pathway Type column. */
function classificationLabel(c: 'grid' | 'btm' | 'hybrid' | undefined): string {
  if (c === 'grid') return 'Grid';
  if (c === 'btm') return 'BTM';
  if (c === 'hybrid') return 'Hybrid';
  return '–';
}

export const CapexOpexBreakdownSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const h = report.financialAnalysis?.headline;
  const pathways = report.pathwayComparisonAtGen?.pathways ?? [];
  const hasData = Boolean(h) || pathways.length > 0;

  const subheadStyle: React.CSSProperties = {
    fontFamily: FIN_MONO,
    color: roleColor(theme, 'muted'),
  };

  if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return <SectionShell label={label} hasData><DataCenterFinancialSnapshot stamp={report.financialAnalysis} section="capexOpexBreakdown" /></SectionShell>;

  return (
    <SectionShell label={label} hasData={hasData}>
      {h ? (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={subheadStyle}>
            Cost intensity
          </div>
          {/* Carrier div holds the testid (EditorialKpiRail has no testid prop). */}
          <div data-testid="capex-opex-cost-rail">
            <EditorialKpiRail
              theme={theme}
              items={[
                { key: 'CAPEX intensity', value: fmtUsdPerKw(h.capexPerKwUsd) },
                { key: 'Annual generation', value: fmtMwh(h.annualMwh) },
                {
                  key: 'Blended LCOE',
                  value: fmtUsdPerMwh(h.lcoeUsdPerMwh),
                  // Q6 / 4.4 — name the basis (see FinancialSummarySection).
                  unit: h.lcoeBasis ? lcoeBasisLabel(h.lcoeBasis) : undefined,
                },
              ]}
            />
          </div>
        </div>
      ) : null}

      {pathways.length > 0 ? (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={subheadStyle}>
            Per-pathway LCOE
          </div>
          {/* data-span-columns: wide table spans both flow columns if enabled.
              Carrier div holds the testid (EditorialDataTable has no testid prop). */}
          <div data-span-columns className="overflow-x-auto" data-testid="capex-opex-pathway-table">
            <EditorialDataTable
              theme={theme}
              columns={[
                { header: 'Pathway' },
                { header: 'Type' },
                { header: 'Headline' },
                { header: 'LCOE', numeric: true, align: 'right' },
              ]}
              rows={pathways.map((p) => [
                p.name,
                <span style={{ color: roleColor(theme, 'muted') }}>{classificationLabel(p.classification)}</span>,
                p.headlineLabel,
                fmtUsdPerMwh(p.lcoeUsdPerMwh),
              ])}
            />
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
};
